import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChangeLangBody } from '../components/language/change-lang-body';
import { useStorage } from '../hooks/use-storage';
import { useSwal } from '../hooks/use-swal';
import { useAnalytics } from '../utils/setup/analytics-setup';

function useChangeLangModal() {
  const storage = useStorage();
  const { setLanguage } = storage;
  const analytics = useAnalytics();
  const { analyticsPageView } = analytics;
  const { language, languages } = useStorage();
  const { swal } = useSwal({ storage });
  const selectedLanguageRef = useRef({});
  const availableLanguagesRef = useRef([]);
  const { t } = useTranslation();

  const didOpen = () => {
    analyticsPageView('Change Language');
  };

  const handleOnClick = (lang) => {
    setLanguage(lang);
  };

  async function loadFlag(lang) {
    const module = await import(
      /* webpackChunkName: "locale-[request]" */ `../assets/images/flags/${lang.tag}.png`
    );
    return module.default;
  }

  async function prepare() {
    selectedLanguageRef.current = {
      ...language,
      flag: await loadFlag(language),
      disabled: false,
    };

    const promises = languages.map((lang) =>
      (async () => {
        lang.flag = await loadFlag(lang);
        lang.disabled = false;

        return lang;
      })()
    );

    availableLanguagesRef.current = await Promise.all(promises);
  }

  async function show() {
    await swal.fire({
      html: (
        <ChangeLangBody
          analytics={analytics}
          selectedLanguage={selectedLanguageRef.current}
          availableLanguages={availableLanguagesRef.current}
          onClick={handleOnClick}
        />
      ),
      didOpen,
      inputAttributes: { autocapitalize: 'on', maxlength: 30 },
      cancelButtonText: t('cancel'),
      showCloseButton: true,
      showCancelButton: false,
      showConfirmButton: false,
      allowOutsideClick: true,
      showClass: {
        popup: 'animate__animated animate__slideInLeft animate__faster',
      },
    });
  }

  return {
    prepareChangeLangModal: prepare,
    showChangeLangModal: show,
  };
}

export { useChangeLangModal };
